import React, { useState, useEffect, useCallback } from 'react';
import chemicalService from '../../../services/chemicalService';
import PaymentFormModal from '../components/PaymentFormModal';

const PAGE_SIZE = 10;

const LedgerTab = () => {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({
    receipts_this_month: 0,
    receipts_count: 0,
    payments_this_month: 0,
    payments_count: 0,
    net_cash_flow: 0,
  });
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'receipt' | 'payment'
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);

  const [showFormModal, setShowFormModal] = useState(false);
  const [formDefaultType, setFormDefaultType] = useState('receipt');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [typeFilter, debouncedSearch, fromDate, toDate]);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: PAGE_SIZE };
      if (typeFilter !== 'all') params.type = typeFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const [listRes, summaryRes] = await Promise.all([
        chemicalService.getPayments(params),
        chemicalService.getPaymentsSummary().catch((e) => {
          console.warn('Payments summary not available:', e.message);
          return { data: { receipts_this_month: 0, receipts_count: 0, payments_this_month: 0, payments_count: 0, net_cash_flow: 0 } };
        }),
      ]);

      setPayments(listRes.data || []);
      setPagination(listRes.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
      setSummary(summaryRes.data || { receipts_this_month: 0, receipts_count: 0, payments_this_month: 0, payments_count: 0, net_cash_flow: 0 });
    } catch (err) {
      console.error('Error fetching payments:', err);
      alert('❌ ' + (err.response?.data?.message || 'Failed to load ledger'));
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter, debouncedSearch, fromDate, toDate]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleDelete = async (p) => {
    const partyLabel = p.party_type === 'customer' ? 'customer' : 'vendor';
    const txnType = p.type === 'receipt' ? 'receipt' : 'payment';
    if (!window.confirm(
      `Delete ${txnType} "${p.ref_no}"?\n\n` +
      `This will reverse the ${partyLabel}'s balance and unsettle the linked ${p.type === 'receipt' ? 'invoice' : 'bill'}. Cannot be undone.`
    )) return;

    try {
      await chemicalService.deletePayment(p.id);
      alert('✅ Transaction deleted, balances reversed.');
      if (payments.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchPayments();
      }
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to delete transaction'));
    }
  };

  const handleSaved = () => {
    setShowFormModal(false);
    fetchPayments();
  };

  const openForm = (defaultType) => {
    setFormDefaultType(defaultType);
    setShowFormModal(true);
  };

  const clearDateFilters = () => {
    setFromDate('');
    setToDate('');
  };

  const netCashFlow = parseFloat(summary.net_cash_flow) || 0;

  return (
    <div style={{ padding: '24px 30px 40px' }}>

      {/* ─── Summary Cards ───────────────────────── */}
      <div style={styles.summaryGrid}>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #27ae60' }}>
          <div style={styles.summaryLabel}>Receipts This Month</div>
          <div style={{ ...styles.summaryValue, color: '#27ae60' }}>
            {formatLakh(summary.receipts_this_month)}
          </div>
          <div style={styles.summarySub}>{summary.receipts_count} transactions</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #e74c3c' }}>
          <div style={styles.summaryLabel}>Payments This Month</div>
          <div style={{ ...styles.summaryValue, color: '#e74c3c' }}>
            {formatLakh(summary.payments_this_month)}
          </div>
          <div style={styles.summarySub}>{summary.payments_count} transactions</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: `4px solid ${netCashFlow >= 0 ? '#3498db' : '#f39c12'}` }}>
          <div style={styles.summaryLabel}>Net Cash Flow</div>
          <div style={{ ...styles.summaryValue, color: netCashFlow >= 0 ? '#3498db' : '#f39c12' }}>
            {netCashFlow >= 0 ? '+' : ''}{formatLakh(Math.abs(netCashFlow))}
          </div>
          <div style={styles.summarySub}>
            {netCashFlow >= 0 ? '↗ Positive month' : '↘ Negative month'}
          </div>
        </div>
      </div>

      {/* ─── Filters + Search + Add ──────────────── */}
      <div style={styles.toolbar}>
        <div style={styles.filterTabs}>
          {[
            { id: 'all', label: 'All' },
            { id: 'receipt', label: '↓ Receipts' },
            { id: 'payment', label: '↑ Payments' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setTypeFilter(f.id)}
              style={{
                ...styles.filterButton,
                background: typeFilter === f.id ? '#e74c3c' : 'white',
                color: typeFilter === f.id ? 'white' : '#2c3e50',
                borderColor: typeFilter === f.id ? '#e74c3c' : '#e0e0e0',
              }}
            >
              {f.label}
            </button>
          ))}

          <div style={styles.dateRangeWrap}>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={styles.dateInput}
            />
            <span style={{ color: '#95a5a6', fontSize: '13px' }}>→</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={styles.dateInput}
            />
            {(fromDate || toDate) && (
              <button onClick={clearDateFilters} style={styles.clearButton}>✕</button>
            )}
          </div>
        </div>

        <div style={styles.toolbarRight}>
          <div style={styles.searchWrap}>
            <span style={styles.searchIcon}>⌕</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search ref no or invoice/bill..."
              style={styles.searchInput}
            />
          </div>
          <button onClick={() => openForm('receipt')} style={styles.receiptButton}>
            ↓ Receipt
          </button>
          <button onClick={() => openForm('payment')} style={styles.paymentButton}>
            ↑ Payment
          </button>
        </div>
      </div>

      {/* ─── Transactions Table ──────────────────── */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Ref No</th>
              <th style={styles.th}>Party</th>
              <th style={styles.th}>Against</th>
              <th style={styles.th}>Mode</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" style={styles.noData}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
                  Loading transactions...
                </td>
              </tr>
            ) : payments.length === 0 ? (
              <tr>
                <td colSpan="8" style={styles.noData}>
                  <div style={{ fontSize: '40px', marginBottom: '8px' }}>💰</div>
                  {debouncedSearch || typeFilter !== 'all' || fromDate || toDate
                    ? 'No transactions match your filters'
                    : 'No transactions yet — record a receipt or payment to get started'}
                </td>
              </tr>
            ) : (
              payments.map((p) => {
                const isReceipt = p.type === 'receipt';
                return (
                  <tr key={p.id} style={styles.row}>
                    <td style={styles.td}>{formatDate(p.date)}</td>
                    <td style={styles.td}>
                      <span style={isReceipt ? styles.receiptBadge : styles.paymentBadge}>
                        {isReceipt ? '↓ Receipt' : '↑ Payment'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.refTag}>{p.ref_no}</span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontWeight: '600', color: '#2c3e50' }}>
                        {p.party_name || '—'}
                      </div>
                      <div style={{ fontSize: '11px', color: '#95a5a6', marginTop: '2px' }}>
                        {p.party_type === 'customer' ? 'Customer' : 'Vendor'}
                      </div>
                    </td>
                    <td style={styles.td}>
                      {p.against_no ? (
                        <span style={styles.againstTag}>{p.against_no}</span>
                      ) : '—'}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.modeBadge}>{formatPaymentMode(p.mode)}</span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      <strong style={{
                        color: isReceipt ? '#27ae60' : '#e74c3c',
                        fontSize: '15px',
                      }}>
                        {isReceipt ? '+ ' : '− '}Rs {formatNumber(p.amount)}
                      </strong>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <button onClick={() => handleDelete(p)} style={styles.deleteButton} title="Delete">
                        🗑️
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ─── Pagination ──────────────────────────── */}
      {!loading && pagination.total > 0 && (
        <div style={styles.paginationWrap}>
          <div style={styles.paginationInfo}>
            Showing {(pagination.page - 1) * pagination.limit + 1}
            –{Math.min(pagination.page * pagination.limit, pagination.total)}
            {' '}of {pagination.total} transactions
          </div>
          <div style={styles.paginationButtons}>
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}
            >
              «
            </button>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}
            >
              ‹ Prev
            </button>
            <span style={styles.pageIndicator}>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= pagination.totalPages}
              style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}
            >
              Next ›
            </button>
            <button
              onClick={() => setPage(pagination.totalPages)}
              disabled={page >= pagination.totalPages}
              style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}
            >
              »
            </button>
          </div>
        </div>
      )}

      {/* ─── Form Modal ──────────────────────────── */}
      {showFormModal && (
        <PaymentFormModal
          defaultType={formDefaultType}
          onClose={() => setShowFormModal(false)}
          onSaved={handleSaved}
        />
      )}

    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatNumber = (n) => {
  const num = parseFloat(n) || 0;
  return num.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const formatLakh = (n) => {
  const num = parseFloat(n) || 0;
  if (num >= 1000000) return `Rs ${(num / 1000000).toFixed(2)}M`;
  return `Rs ${formatNumber(num)}`;
};

const formatPaymentMode = (m) => {
  const map = {
    cash: 'Cash',
    bank_transfer: 'Bank',
    cheque: 'Cheque',
    easypaisa: 'EasyPaisa',
    jazzcash: 'JazzCash',
  };
  return map[m] || m;
};

// ─── Styles ───────────────────────────────────────────────

const styles = {
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  summaryCard: {
    background: 'white',
    padding: '18px 22px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  summaryLabel: { fontSize: '13px', color: '#7f8c8d', marginBottom: '6px', fontWeight: '600' },
  summaryValue: { fontSize: '28px', fontWeight: '700' },
  summarySub: { fontSize: '11px', color: '#95a5a6', marginTop: '4px' },

  toolbar: {
    background: 'white',
    padding: '16px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex', flexWrap: 'wrap', gap: '16px',
    justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '16px',
  },
  filterTabs: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' },
  filterButton: {
    padding: '8px 16px',
    border: '2px solid', borderRadius: '8px',
    fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', transition: 'all 0.2s',
  },
  dateRangeWrap: {
    display: 'flex', alignItems: 'center', gap: '6px',
    paddingLeft: '12px', marginLeft: '4px',
    borderLeft: '1px solid #e0e0e0',
  },
  dateInput: {
    padding: '8px 10px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '12px', fontFamily: 'inherit',
  },
  clearButton: {
    padding: '4px 10px',
    background: '#f0f0f0', color: '#7f8c8d',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '13px', fontWeight: '700',
  },
  toolbarRight: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' },
  searchWrap: { position: 'relative' },
  searchIcon: {
    position: 'absolute', left: '12px', top: '50%',
    transform: 'translateY(-50%)',
    color: '#95a5a6', fontSize: '16px',
  },
  searchInput: {
    padding: '10px 12px 10px 36px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', width: '240px',
    fontFamily: 'inherit',
  },
  receiptButton: {
    padding: '10px 18px',
    background: '#27ae60', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '700',
  },
  paymentButton: {
    padding: '10px 18px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '700',
  },

  tableWrap: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    overflow: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  headerRow: { background: '#f8f9fa' },
  th: {
    padding: '14px 16px', textAlign: 'left',
    fontSize: '13px', fontWeight: '700',
    color: '#2c3e50', borderBottom: '2px solid #e0e0e0',
    whiteSpace: 'nowrap',
  },
  row: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '12px 16px', fontSize: '14px', color: '#2c3e50' },
  noData: {
    padding: '60px 20px', textAlign: 'center',
    color: '#95a5a6', fontSize: '14px',
  },

  receiptBadge: {
    background: '#d4edda', color: '#155724',
    padding: '4px 12px', borderRadius: '12px',
    fontSize: '12px', fontWeight: '700',
    whiteSpace: 'nowrap',
  },
  paymentBadge: {
    background: '#f8d7da', color: '#721c24',
    padding: '4px 12px', borderRadius: '12px',
    fontSize: '12px', fontWeight: '700',
    whiteSpace: 'nowrap',
  },
  refTag: {
    background: '#fff3cd', color: '#856404',
    padding: '4px 10px', borderRadius: '10px',
    fontSize: '12px', fontWeight: '700',
    fontFamily: 'monospace',
  },
  againstTag: {
    background: '#e3f2fd', color: '#1565c0',
    padding: '4px 10px', borderRadius: '10px',
    fontSize: '12px', fontWeight: '600',
    fontFamily: 'monospace',
  },
  modeBadge: {
    background: '#f0f0f0', color: '#555',
    padding: '4px 10px', borderRadius: '10px',
    fontSize: '12px', fontWeight: '600',
  },

  deleteButton: {
    padding: '6px 10px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px',
  },

  paginationWrap: {
    marginTop: '16px',
    background: 'white',
    padding: '14px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', flexWrap: 'wrap', gap: '12px',
  },
  paginationInfo: { fontSize: '13px', color: '#7f8c8d' },
  paginationButtons: { display: 'flex', gap: '6px', alignItems: 'center' },
  pageButton: {
    padding: '8px 14px',
    background: 'white', color: '#2c3e50',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    cursor: 'pointer', fontSize: '13px', fontWeight: '600',
  },
  pageButtonDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  pageIndicator: {
    padding: '0 12px', fontSize: '13px',
    color: '#2c3e50', fontWeight: '600',
  },
};

export default LedgerTab;